// El equipo tal como lo ve quien mira, calculado en UN sitio: su bandeja, los grupos de la lista (con «Con Santi»), el
// orden plano y quién le necesita. Lo leen la lista, el riel plegado, «Saltar a…» y Alt+↑/↓ del chat: antes cada uno
// lo calculaba a su manera y el teclado se saltaba filas (04-11, auditoría de código B1/B2).
//
// El chat abierto conserva su sitio: al abrirlo el servidor lo marca leído y, con «lo no leído primero», su fila
// saltaría bajo el cursor. El almacén guarda cómo estaba al abrirlo (`noLeidoAlAbrir`) y todos ordenan con eso.
import { useMemo } from 'react';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { useOficinaStore } from '../store';
import { agruparPorSituacion, necesitaAPau, resumenCabecera } from './situacion';
import { useBandeja } from './useBandeja';

export function useEquipo(contratados: Record<string, ContratadoDTO>) {
  const bandeja = useBandeja(contratados);
  const santi = useSantiPorAgente();
  const congelado = useOficinaStore((s) => s.noLeidoAlAbrir);
  const { visibles } = bandeja;
  const grupos = useMemo(
    () => agruparPorSituacion(visibles, (c) => (congelado && c.agentId === congelado.agentId ? congelado.noLeido : c.noLeido), santi),
    [visibles, congelado, santi],
  );
  const orden = useMemo(() => grupos.flatMap((g) => g.miembros), [grupos]);
  const necesita = useMemo(() => {
    const ids = new Set(orden.filter((c) => necesitaAPau(c, santi.get(c.agentId))).map((c) => c.agentId));
    return (c: Pick<ContratadoDTO, 'agentId'>) => ids.has(c.agentId);
  }, [orden, santi]);
  const resumen = useMemo(() => resumenCabecera(visibles, santi), [visibles, santi]);
  return { ...bandeja, santi, grupos, orden, necesita, resumen };
}

/**
 * Alt+↓ / Alt+↑: el siguiente (o anterior) que te necesita en el orden de la lista, sin contar el abierto y dando la
 * vuelta. Con `todos`, el siguiente sin más (Alt+Mayús). null = no queda nadie (Nada más te toca).
 */
export function siguienteEnEquipo<C extends Pick<ContratadoDTO, 'agentId'>>(orden: readonly C[], desde: string | null, paso: 1 | -1, cuenta: (c: C) => boolean): C | null {
  const n = orden.length;
  if (!n) return null;
  const i = orden.findIndex((c) => c.agentId === desde);
  for (let k = 1; k <= n; k += 1) {
    const c = orden[(((i < 0 ? (paso === 1 ? -1 : 0) : i) + paso * k) % n + n) % n]!;
    if (c.agentId !== desde && cuenta(c)) return c;
  }
  return null;
}
